import * as React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import GoalConfig, { TYPES } from 'modules/goals/store/models/GoalConfig'

class GoalConfigForm extends Form {
  constructor(values: {}) {
    super(values, {
      name: 'string',
      description: 'string',
      start_date: 'date',
      due_date: 'date',
      goal_type: 'string',
    })
  }
}

const MAX_GOAL_NAME_LENGHT = 50

export const NewGoalModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const form = React.useRef(
    new GoalConfigForm({
      goal_type: TYPES.numeric,
    })
  ).current

  const goalConfig = new GoalConfig()
  const [disabled, setDisabled] = React.useState(true)

  const options = [
    {
      value: TYPES.numeric,
      label: i18n.t('surveys.goals.types.numeric'),
    },
    {
      value: TYPES.none,
      label: i18n.t('surveys.goals.types.none'),
    },
  ]

  const { performAction } = useAsyncAction(async () => {
    await form.save(goalConfig)
    onClose()
    navigate(navigation.performance.goals.id.fullPath({ id: goalConfig.id }))
  })

  const checkContent = (value: string) => {
    return value ? setDisabled(false) : setDisabled(true)
  }

  if (!users.current.policy.can('apiCore.accessGoalsConfigCreate')) {
    return <Navigate to={navigation.performance.goals.fullPath()}></Navigate>
  }

  const validateNameLenght = (name: string) => {
    return name.length <= MAX_GOAL_NAME_LENGHT
      ? ''
      : i18n.t('surveys.goals.new.errors.name_too_long', {
          max_characters: MAX_GOAL_NAME_LENGHT,
        })
  }

  return (
    <Modal
      illustration="add-block"
      title={i18n.t('surveys.goals.new.title')}
      description={i18n.t('surveys.goals.new.description')}
      onClose={onClose}
      onSubmit={performAction}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={disabled} submit>
            {i18n.t('surveys.goals.new.create')}
          </Button>
        </>
      }
    >
      <FormRow>
        <Text
          label={i18n.t('surveys.goals.new.goal_name')}
          field={form.get('name')}
          mandatory
          onChange={() => checkContent(form.get('name').value)}
          validation={validateNameLenght}
        />
      </FormRow>
      <FormRow>
        <TextArea
          label={i18n.t('surveys.goals.new.goal_description')}
          field={form.get('description')}
        />
      </FormRow>
      <FormRow multi>
        <DayPicker
          label={i18n.t('surveys.goals.common.start_date')}
          field={form.get('start_date')}
        />
        <DayPicker label={i18n.t('surveys.goals.common.due_date')} field={form.get('due_date')} />
      </FormRow>
      <FormRow>
        <Select
          label={i18n.t('surveys.goals.new.type')}
          field={form.get('goal_type')}
          options={options}
          showOriginalLabelText
        />
      </FormRow>
    </Modal>
  )
}
